import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Badge,
  ToggleButton,
  ToggleButtonGroup,
  AppBar,
  Toolbar,
  useTheme,
  alpha,
  useMediaQuery,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  TrendingUp as TrendingIcon,
  Refresh as RefreshIcon,
  Menu as MenuIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  AccessTime as CountdownIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  Download as DownloadIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';

import { theme as appTheme } from '../../theme';
import { useViewMode } from '../../contexts/ViewModeContext';
import { useUserRole } from '../../contexts/UserRoleContext';
import { useRefresh } from '../../contexts/RefreshContext';
import { useLayoutActions } from '../../contexts/LayoutActionsContext';
import { useSearchIntercept, HeaderSearchResult } from '../../contexts/SearchInterceptContext';
import DataService, { Election } from '../../services/dataService';
import { useNavItems } from './NavItems';
import { UnifiedFilter } from './UnifiedFilter';
import { useBreadcrumbText, HeaderBreadcrumb } from './HeaderBreadcrumb';
import { NotificationsMenu, TrendingMenu, ActionsMenu } from './HeaderMenus';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrendingCandidate {
  id: number;
  name: string;
  party: string;
  trend: 'up' | 'down';
  mentions: string;
  change: string;
  avatar: string;
  profileImage?: string;
  color: string;
}

interface NotificationItem {
  id: number;
  title: string;
  description: string;
  time: string;
  type: string;
  read: boolean;
}

// ---------------------------------------------------------------------------
// Search result type / display maps
// ---------------------------------------------------------------------------

const RESULT_TYPE_LABELS: Record<string, string> = {
  candidate: 'Candidate',
  actor: 'Political Actor',
  election: 'Election',
};
const RESULT_TYPE_COLORS: Record<string, string> = {
  candidate: '#084608',
  actor: '#e67e22',
  election: '#1976d2',
};

// ---------------------------------------------------------------------------
// AppHeader props
// ---------------------------------------------------------------------------

interface AppHeaderProps {
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  onMobileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

// ---------------------------------------------------------------------------
// AppHeader
// ---------------------------------------------------------------------------

export const AppHeader: React.FC<AppHeaderProps> = ({
  sidebarCollapsed,
  setSidebarCollapsed,
  onMobileMenuOpen,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { viewMode, setViewMode } = useViewMode();
  const { refresh, refreshKey } = useRefresh();
  const { hasFeature } = useUserRole();
  const { actions: pageActions } = useLayoutActions();
  const { handler: searchIntercept } = useSearchIntercept();
  const menuItems = useNavItems();
  const getBreadcrumbText = useBreadcrumbText(menuItems);

  const actionsButtonRef = useRef<HTMLButtonElement>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchResults, setSearchResults] = useState<Array<Record<string, unknown>>>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Refresh animation state
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Menu anchors
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  const [trendingAnchorEl, setTrendingAnchorEl] = useState<null | HTMLElement>(null);
  const [actionsMenuAnchorEl, setActionsMenuAnchorEl] = useState<null | HTMLElement>(null);

  // Election countdown
  const [nextElectionCountdown, setNextElectionCountdown] = useState<{
    days: number;
    hours: number;
    label: string;
  } | null>(null);

  // Trending candidates
  const [trendingCandidates, setTrendingCandidates] = useState<TrendingCandidate[]>([]);

  const notifications: NotificationItem[] = [];
  const unreadCount = notifications.filter((n) => !n.read).length;

  // Countdown timer effect
  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    const elections: Election[] = DataService.getElections();
    const upcoming = elections
      .filter((e) => new Date(e.date) > new Date())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    if (upcoming.length) {
      const next = upcoming[0];
      const update = () => {
        const diff = new Date(next.date).getTime() - Date.now();
        if (diff <= 0) {
          setNextElectionCountdown(null);
          return;
        }
        setNextElectionCountdown({
          days: Math.floor(diff / 86400000),
          hours: Math.floor((diff % 86400000) / 3600000),
          label: next.title,
        });
      };
      update();
      timer = setInterval(update, 60000);
    }
    return () => clearInterval(timer);
  }, []);

  // Trending candidates effect
  useEffect(() => {
    let cancelled = false;
    fetch('/api/candidates/trending?limit=3&election=presidential')
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((payload) => {
        if (cancelled) return;
        const palette = [
          theme.palette.primary.main,
          theme.palette.secondary.main,
          theme.palette.info.main,
        ];
        setTrendingCandidates(
          (payload.candidates ?? []).map(
            (c: Record<string, unknown>, i: number) => {
              const mentions24h = c.mentions24h as number;
              const mentions7dPrior = c.mentions7dPrior as number;
              const trendPct = c.trendPct as number;
              // No signal in either window — "+0%" with a green up-arrow would
              // misleadingly imply momentum that doesn't exist.
              const noSignal = mentions24h === 0 && mentions7dPrior === 0;
              const trend = noSignal ? 'flat' : trendPct > 0 ? 'up' : trendPct < 0 ? 'down' : 'flat';
              return {
              id: c.id,
              name: c.name,
              party: c.party,
              trend,
              mentions: `${mentions24h} mentions / 24h`,
              change: noSignal ? '—' : `${trendPct > 0 ? '+' : ''}${trendPct}%`,
              avatar: ((c.name as string) ?? '')
                .split(' ')
                .map((n: string) => n[0])
                .join(''),
              profileImage: c.profileImage as string | undefined,
              color: palette[i % palette.length],
              };
            },
          ),
        );
      })
      .catch(() => {
        if (!cancelled) setTrendingCandidates([]);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, theme]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    refresh();
    setTimeout(() => setIsRefreshing(false), 800);
  };

  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (value.trim().length < 2) {
      setSearchResults([]);
      setSearchOpen(false);
      return;
    }
    searchDebounce.current = setTimeout(async () => {
      try {
        const r = await fetch(`/api/search?q=${encodeURIComponent(value.trim())}&limit=10`);
        if (r.ok) {
          const data = await r.json();
          setSearchResults(data);
          setSearchOpen(data.length > 0);
        }
      } catch {
        /* silent */
      }
    }, 150);
  }, []);

  const handleSearchSelect = (result: Record<string, unknown>) => {
    setSearchQuery('');
    setSearchOpen(false);
    setSearchResults([]);
    // Let the current page (e.g. the political network graph) claim the selection first —
    // it returns true if it jumped to the entity itself, so we skip the default navigation.
    if (searchIntercept?.(result as unknown as HeaderSearchResult)) return;
    navigate((result.path as string) ?? '/candidates');
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && searchQuery.trim()) {
      setSearchOpen(false);
      navigate(`/candidates?search=${encodeURIComponent(searchQuery.trim())}`);
    }
    if (event.key === 'Escape') {
      setSearchOpen(false);
    }
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={1}
        sx={{
          backgroundColor: 'background.paper',
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pl: 0.5,
            pr: 1,
            minHeight: 64,
          }}
        >
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label="open menu"
              edge="start"
              onClick={onMobileMenuOpen}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Desktop Sidebar Toggle */}
          {!isMobile && (
            <IconButton
              color="inherit"
              aria-label="toggle sidebar"
              edge="start"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              sx={{ mr: 0.5 }}
              title={sidebarCollapsed ? t('header.expandSidebar') : t('header.collapseSidebar')}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo + Breadcrumb */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                color: theme.palette.primary.main,
                fontFamily: '"Libre Baskerville", Georgia, serif',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Electure
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HeaderBreadcrumb menuItems={menuItems} getBreadcrumbText={getBreadcrumbText} />
            </Box>
          </Box>

          {/* Search Bar + Filters */}
          <Box
            ref={searchRef}
            sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'column',
              flexGrow: 1,
              maxWidth: { sm: 300, md: 420, lg: 500 },
              mx: { xs: 0.5, sm: 1, md: 2 },
              gap: 0.75,
              position: 'relative',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                border: '1px solid',
                borderColor: searchFocused ? 'primary.main' : 'divider',
                borderRadius: 2,
                bgcolor: 'background.default',
                px: 1.5,
                transition: 'all 0.2s ease',
                ...(searchFocused
                  ? { boxShadow: `0 0 0 3px ${alpha(appTheme.palette.primary.main, 0.1)}` }
                  : {}),
              }}
            >
              <Box
                component="input"
                placeholder={t('header.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => handleSearchInput(e.target.value)}
                onKeyDown={handleSearchKeyDown}
                onFocus={() => {
                  setSearchFocused(true);
                  if (searchResults.length > 0) setSearchOpen(true);
                }}
                onBlur={() => {
                  setSearchFocused(false);
                  setTimeout(() => setSearchOpen(false), 150);
                }}
                sx={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  bgcolor: 'transparent',
                  py: 1.5,
                  fontSize: '0.875rem',
                  '&::placeholder': { opacity: 0.6 },
                }}
              />
              <UnifiedFilter />
            </Box>

            {/* Search results dropdown */}
            {searchOpen && searchResults.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  mt: 0.5,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  boxShadow: 4,
                  zIndex: 1400,
                  overflow: 'hidden',
                }}
              >
                {searchResults.map((result, i) => (
                  <Box
                    key={`${result.type}-${result.id}-${i}`}
                    onMouseDown={() => handleSearchSelect(result)}
                    sx={{
                      px: 2,
                      py: 1.25,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      borderBottom: i < searchResults.length - 1 ? '1px solid' : 'none',
                      borderColor: 'divider',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        flexShrink: 0,
                        bgcolor: RESULT_TYPE_COLORS[result.type as string] ?? '#888',
                      }}
                    />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {result.label as string}
                      </Typography>
                      {result.sub && (
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {result.sub as string}
                          {result.extra ? ` · ${result.extra as string}` : ''}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={RESULT_TYPE_LABELS[result.type as string] ?? (result.type as string)}
                      size="small"
                      sx={{
                        ml: 'auto',
                        fontSize: '0.65rem',
                        height: 18,
                        flexShrink: 0,
                        bgcolor: alpha(RESULT_TYPE_COLORS[result.type as string] ?? '#888', 0.1),
                        color: RESULT_TYPE_COLORS[result.type as string] ?? '#888',
                      }}
                    />
                  </Box>
                ))}
                <Box
                  onMouseDown={() => {
                    navigate(`/candidates?search=${encodeURIComponent(searchQuery.trim())}`);
                    setSearchOpen(false);
                  }}
                  sx={{
                    px: 2,
                    py: 1,
                    bgcolor: 'action.hover',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.selected' },
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    See all results for "{searchQuery}"
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>

          {/* Page-specific actions injected via useLayoutActions() */}
          {pageActions}

          {/* Detail / Data Insights page actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 1 } }}>
            {(location.pathname.startsWith('/manifesto/') ||
              location.pathname.startsWith('/election/')) && (
              <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                <IconButton
                  size="small"
                  title="Share"
                  onClick={() => {
                    if (navigator.share) {
                      navigator.share({ title: document.title, url: window.location.href });
                    } else {
                      navigator.clipboard.writeText(window.location.href);
                    }
                  }}
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  title="Bookmark"
                  sx={{
                    color: 'text.secondary',
                    '&:hover': { color: 'primary.main', bgcolor: 'rgba(0,0,0,0.04)' },
                  }}
                >
                  <BookmarkIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            {location.pathname === '/data-insights' && (
              <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                <IconButton
                  size="small"
                  sx={{
                    borderRadius: 20,
                    borderColor: '1px solid',
                    color: 'text.secondary',
                    px: 1,
                    py: 0.5,
                    '&:hover': {
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      bgcolor: 'rgba(0,0,0,0.04)',
                    },
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  sx={{
                    borderRadius: 20,
                    bgcolor: 'primary.main',
                    px: 1,
                    py: 0.5,
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                  }}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          {/* Right-side icons */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.25, sm: 0.5 },
              pr: { xs: 0.5, sm: 1 },
            }}
          >
            {/* View Mode Toggle */}
            {['/manifestos', '/candidates'].includes(location.pathname) && !isMobile && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, next) => next && setViewMode(next)}
                size="small"
                sx={{ bgcolor: 'white', mr: { xs: 0, sm: 0.5 } }}
              >
                <ToggleButton value="card" aria-label="card view" sx={{ px: 1, py: 0.5 }}>
                  <ViewModuleIcon fontSize="small" />
                </ToggleButton>
                <ToggleButton value="table" aria-label="table view" sx={{ px: 1, py: 0.5 }}>
                  <ViewListIcon fontSize="small" />
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {/* Countdown Chip */}
            {nextElectionCountdown && hasFeature('countdown') && (
              <Box
                onClick={() => navigate('/elections')}
                sx={{
                  display: { xs: 'none', sm: 'flex' },
                  alignItems: 'center',
                  gap: 1,
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'primary.dark' },
                  transition: 'background 0.15s',
                }}
              >
                <CountdownIcon sx={{ fontSize: 20, color: 'success.main' }} />
                <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: '#fff', lineHeight: 1 }}>
                  {nextElectionCountdown.days}d {nextElectionCountdown.hours}h
                </Typography>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'rgba(255,255,255,0.7)',
                    lineHeight: 1,
                  }}
                >
                  to {nextElectionCountdown.label.replace(/ Election.*/, '').substring(0, 14)}
                </Typography>
              </Box>
            )}

            {/* Global Refresh */}
            <IconButton
              size={isMobile ? 'small' : 'large'}
              onClick={handleRefresh}
              title="Refresh page data"
              sx={{
                transition: 'transform 0.6s ease',
                transform: isRefreshing ? 'rotate(360deg)' : 'none',
              }}
            >
              <RefreshIcon />
            </IconButton>

            {/* Actions Dropdown Trigger */}
            <IconButton
              ref={actionsButtonRef}
              size={isMobile ? 'small' : 'large'}
              onClick={(e) => setActionsMenuAnchorEl(e.currentTarget)}
              title="Actions"
            >
              <MoreVertIcon />
            </IconButton>

            {/* Notifications icon (direct access) */}
            <IconButton
              size={isMobile ? 'small' : 'large'}
              onClick={(e) => setNotificationsAnchorEl(e.currentTarget)}
              title={t('notifications.title')}
            >
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsIcon />
              </Badge>
            </IconButton>

            {/* Trending icon (direct access) */}
            <IconButton
              size={isMobile ? 'small' : 'large'}
              onClick={(e) => setTrendingAnchorEl(e.currentTarget)}
              title={t('trending.title')}
            >
              <Badge badgeContent={trendingCandidates.length} color="primary">
                <TrendingIcon />
              </Badge>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Dropdown Menus */}
      <NotificationsMenu
        anchorEl={notificationsAnchorEl}
        notifications={notifications}
        onClose={() => setNotificationsAnchorEl(null)}
      />

      <TrendingMenu
        anchorEl={trendingAnchorEl}
        candidates={trendingCandidates}
        onClose={() => setTrendingAnchorEl(null)}
      />

      <ActionsMenu
        anchorEl={actionsMenuAnchorEl}
        actionsButtonRef={actionsButtonRef}
        unreadCount={unreadCount}
        trendingCount={trendingCandidates.length}
        onClose={() => setActionsMenuAnchorEl(null)}
        onOpenNotifications={(el) => setNotificationsAnchorEl(el)}
        onOpenTrending={(el) => setTrendingAnchorEl(el)}
      />
    </>
  );
};
